import { planesToObb } from "@/hooks/use-clipping-planes";
import { Plane, Vector3 } from "three";
import { toMapLocal, WorldMapPose } from "./world-map-alignment";
import { GeoPosition, mapLocalToGeo } from "./world-map-tiles";

type LngLat = [number, number];

function cross(o: LngLat, a: LngLat, b: LngLat): number {
  return (a[0] - o[0]) * (b[1] - o[1]) - (a[1] - o[1]) * (b[0] - o[0]);
}

/**
 * @param points The points to enclose, sorted along the sweep direction
 * @returns One half of the convex hull, without its last point
 */
function halfHull(points: LngLat[]): LngLat[] {
  const hull: LngLat[] = [];
  for (const point of points) {
    while (
      hull.length >= 2 &&
      cross(hull[hull.length - 2], hull[hull.length - 1], point) <= 0
    ) {
      hull.pop();
    }
    hull.push(point);
  }
  return hull.slice(0, -1);
}

/**
 * @param points The points to enclose
 * @returns The convex hull of the points, counter-clockwise (Andrew's monotone chain)
 */
function convexHull(points: LngLat[]): LngLat[] {
  const sorted = [...points].sort((a, b) => a[0] - b[0] || a[1] - b[1]);
  return [...halfHull(sorted), ...halfHull(sorted.reverse())];
}

/**
 * @param planes The clipping planes of the scene
 * @param origin The project coordinates
 * @param pose The placement of the map in the scene
 * @returns The outline of the clipping box seen from above, as a closed clockwise ring of [longitude, latitude],
 * or undefined if the planes do not describe a box
 */
export function clippingBoxFootprint(
  planes: Plane[],
  origin: GeoPosition,
  pose: WorldMapPose,
): LngLat[] | undefined {
  const box = planesToObb(planes);
  if (!box) return;
  const corners: LngLat[] = [];
  for (const sx of [-0.5, 0.5]) {
    for (const sy of [-0.5, 0.5]) {
      for (const sz of [-0.5, 0.5]) {
        const corner = new Vector3(sx, sy, sz)
          .multiply(box.size)
          .applyQuaternion(box.quaternion)
          .add(box.position);
        const [east, z] = toMapLocal(pose, [corner.x, corner.z]);
        const { lng, lat } = mapLocalToGeo(origin, east, -z);
        corners.push([lng, lat]);
      }
    }
  }
  // ArcGIS expects outer rings clockwise
  const ring = convexHull(corners).reverse();
  return [...ring, ring[0]];
}
